import { LocalFile } from '@/types/knowledge';
import { CheckCircle2, Clock, File, Loader2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

// interface LocalFilesProps {
//     knowledgeSetId: string;
// }

// Mock data for local files
const mockFiles: LocalFile[] = [
    {
        id: '1',
        name: 'product_guide.pdf',
        size: 2456789,
        status: 'Processed',
    },
    {
        id: '2',
        name: 'user_manual.pdf',
        size: 1234567,
        status: 'Processing',
    },
    {
        id: '3',
        name: 'faq_document.pdf',
        size: 567890,
        status: 'Pending',
    },
];

export const LocalFiles = () => {
    const [files, setFiles] = useState<LocalFile[]>(mockFiles);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    };

    const getStatusIcon = (status: LocalFile['status']) => {
        switch (status) {
            case 'Processed':
                return <CheckCircle2 size={16} className="text-secondary" />;
            case 'Processing':
                return <Loader2 size={16} className="text-primary animate-spin" />;
            case 'Pending':
                return <Clock size={16} className="text-muted-foreground" />;
        }
    };

    const getStatusColor = (status: LocalFile['status']) => {
        switch (status) {
            case 'Processed':
                return 'text-secondary bg-secondary/10';
            case 'Processing':
                return 'text-primary bg-primary/10';
            case 'Pending':
                return 'text-muted-foreground bg-muted/60';
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files);
        // Mock: Add new files to the list
        const newFiles: LocalFile[] = droppedFiles.map((file, index) => ({
            id: `new-${Date.now()}-${index}`,
            name: file.name,
            size: file.size,
            status: 'Pending' as const,
        }));

        setFiles([...files, ...newFiles]);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files);
            const newFiles: LocalFile[] = selectedFiles.map((file, index) => ({
                id: `new-${Date.now()}-${index}`,
                name: file.name,
                size: file.size,
                status: 'Pending' as const,
            }));

            setFiles([...files, ...newFiles]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const triggerFileUpload = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div className="w-full">
            {/* Drag and Drop Area */}
            <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 mb-6 transition-colors ${
                    isDragging
                        ? 'border-primary bg-primary/10'
                        : 'border-muted-foreground bg-transparent'
                }`}
            >
                <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={handleFileInput}
                    className="hidden"
                />
                <div className="flex flex-col items-center justify-center">
                    <Upload
                        size={48}
                        className={`mb-4 ${isDragging ? "text-primary" : "text-muted-foreground"}`}
                    />
                    <p className="text-lg font-medium mb-2 text-foreground">
                        {isDragging ? 'Drop files here' : 'Drag and drop files here'}
                    </p>
                    <p className="text-sm mb-4 text-muted-foreground">
                        or
                    </p>
                    <button
                        onClick={triggerFileUpload}
                        className="px-4 py-2 rounded-lg transition-colors bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                        Browse Files
                    </button>
                </div>
            </div>

            {/* Files List */}
            <div
                className="overflow-x-auto rounded-lg shadow bg-background"
            >
                <table
                    className="min-w-full divide-y bg-background"
                >
                    <thead>
                        <tr>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                File Name
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Size
                            </th>
                            <th
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                            >
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {files.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-6 py-8 text-center">
                                    <p className="text-muted-foreground">No files uploaded yet</p>
                                </td>
                            </tr>
                        ) : (
                            files.map((file) => (
                                <tr key={file.id} className="hover:bg-muted/50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <File size={20} className="mr-3 text-muted-foreground" />
                                            <span className="text-sm font-medium text-foreground">
                                                {file.name}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-muted-foreground">
                                            {formatFileSize(file.size)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <span
                                                className={`px-2 py-1 inline-flex items-center text-xs font-bold rounded-full ${getStatusColor(
                                                    file.status
                                                )}`}
                                            >
                                                {getStatusIcon(file.status)}
                                                <span className="ml-1">{file.status}</span>
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
